import React from 'react';
import PropTypes from 'prop-types';

const UserAvatar = ({
  user,
  size = 'w-10 h-10',
  variant = 'default',
  useInitials = false,
  bgColor = null,
}) => {
  const getInitials = () => {
    if (!user) return 'A';

    // Handle admin format (fullName)
    if (user.fullName) {
      const names = user.fullName.split(' ').filter(name => name.trim() !== '');
      const firstInitial = names[0] ? names[0][0].toUpperCase() : '';
      const lastInitial = names.length > 1 ? names[names.length - 1][0].toUpperCase() : '';
      return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial || 'A';
    }

    // Handle regular user format (firstName + lastName)
    const firstInitial = user.firstName ? user.firstName[0].toUpperCase() : '';
    const lastInitial = user.lastName ? user.lastName[0].toUpperCase() : '';
    return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial || 'A';
  };

  const getRandomColor = () => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
    ];
    // Use fullName or firstName for color consistency
    const name = user?.fullName || user?.firstName || 'A';
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Map variants to rounding classes
  const roundingStyles = {
    default: 'rounded-full', // Default rounded style
    xl: 'rounded-xl', // Custom rounded style
    square: 'rounded-none', // No rounding (square)
  };

  const baseClasses = `${size} flex items-center justify-center text-white font-medium`;

  // Determine background style based on props
  let bgStyle;
  if (bgColor === 'black') {
    bgStyle = 'bg-black text-white';
  } else if (bgColor) {
    bgStyle = bgColor; // Use custom bgColor if provided
  } else if (variant === 'support') {
    bgStyle = 'bg-black text-white';
  } else {
    bgStyle = getRandomColor();
  }

  const finalStyle = `${bgStyle} ${roundingStyles[variant] || roundingStyles.default}`;

  // Get profile picture URL from either field
  const profilePicUrl = user?.profilePictureUrl || user?.image;

  // Show initials if useInitials is true OR if no profile picture is available
  if (useInitials || !profilePicUrl) {
    return <div className={`${baseClasses} ${finalStyle}`}>{getInitials()}</div>;
  }

  // Show profile picture if available and useInitials is false
  return (
    <img
      src={profilePicUrl}
      alt={`${user.firstName || user.fullName || 'Anonymous'}'s avatar`}
      className={`${size} object-cover ${roundingStyles[variant] || roundingStyles.default}`}
      onError={e => {
        // Fallback to initials if image fails to load
        e.target.style.display = 'none';
      }}
    />
  );
};

UserAvatar.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    fullName: PropTypes.string,
    profilePictureUrl: PropTypes.string,
    image: PropTypes.string,
  }),
  size: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'xl', 'square']),
  useInitials: PropTypes.bool,
  bgColor: PropTypes.string,
};

UserAvatar.defaultProps = {
  user: null,
  size: 'w-10 h-10',
  variant: 'default',
  useInitials: false,
  bgColor: null,
};

export default UserAvatar;
